import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheck, FaTimes, FaCrown, FaRocket, FaBriefcase, FaUsers } from 'react-icons/fa';
import { enhancedPlans, Subscriptions } from '../features/payments/enhanced-pricing-plans.type';

interface PlanComparisonTableProps {
  isOpen: boolean;
  onClose: () => void;
}

const PlanComparisonTable: React.FC<PlanComparisonTableProps> = ({ isOpen, onClose }) => {

  const features = [
    {
      category: 'Interview Practice',
      items: [
        { name: 'Practice Sessions', key: 'sessions' },
        { name: 'Recording Length', key: 'recordingLength' },
        { name: 'Email Reminders', key: 'emailReminder' },
        { name: 'File Upload', key: 'fileUpload' }
      ]
    },
    {
      category: 'Job Analysis',
      items: [
        { name: 'Job Analysis Sessions', key: 'jobAnalysisSessions' },
        { name: 'Company Research', key: 'companyResearch' },
        { name: 'Skills Gap Analysis', key: 'skillsGapAnalysis' },
        { name: 'ATS Optimization', key: 'atsOptimization' }
      ]
    }
    // Export Options category removed - all plans include PDF + DOCX
  ];

  const getFeatureValue = (plan: any, key: string) => {
    const value = plan[key];
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (key === 'skillsGapAnalysis') {
      return value === 'none' ? 'No' : value.charAt(0).toUpperCase() + value.slice(1);
    }
    if (key === 'exportFormats') {
      return Array.isArray(value) ? value.length : 0;
    }
    if (value === -1) {
      return 'Unlimited';
    }
    return value;
  };

  const getFeatureIcon = (plan: any, key: string) => {
    const value = plan[key];
    if (typeof value === 'boolean') {
      return value ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-500" />;
    }
    if (key === 'skillsGapAnalysis') {
      return value === 'none' ? <FaTimes className="text-red-500" /> : <FaCheck className="text-green-500" />;
    }
    if (value && value !== 0 && value !== 'No') {
      return <FaCheck className="text-green-500" />;
    }
    return <FaTimes className="text-red-500" />;
  };

  const planIcons = {
    [Subscriptions.free]: <FaRocket className="text-blue-400" />,
    [Subscriptions.basic]: <FaBriefcase className="text-green-400" />,
    [Subscriptions.pro]: <FaCrown className="text-yellow-400" />,
    [Subscriptions.enterprise]: <FaUsers className="text-purple-400" />
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <motion.div
              className="relative bg-white rounded-3xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-3xl font-bold text-white">Plan Comparison</h2>
                    <p className="text-indigo-100 mt-2">Compare all features across our plans</p>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  >
                    <FaTimes className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Plan Headers */}
                <div className="grid grid-cols-5 gap-4 mb-6">
                  <div className="text-sm font-semibold text-gray-600">Features</div>
                  {enhancedPlans.map((plan, index) => (
                    <motion.div
                      key={plan.name}
                      className={`text-center p-4 rounded-xl ${plan.name === Subscriptions.pro
                        ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400'
                        : 'bg-gray-50'
                        }`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="flex items-center justify-center mb-2">
                        {planIcons[plan.name as keyof typeof planIcons]}
                      </div>
                      <h3 className="font-bold text-lg text-gray-800">{plan.name}</h3>
                      <div className="text-2xl font-bold text-purple-600 mt-2">
                        {plan.amount === 0 ? 'Free' : `$${plan.amount}`}
                      </div>
                      {plan.name === Subscriptions.pro && (
                        <div className="bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold mt-2">
                          MOST POPULAR
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* Feature Comparison */}
                <div className="space-y-6">
                  {features.map((category, categoryIndex) => (
                    <motion.div
                      key={category.category}
                      className="bg-gray-50 rounded-xl p-4"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: categoryIndex * 0.1 }}
                    >
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">{category.category}</h3>
                      <div className="space-y-3">
                        {category.items.map((item, itemIndex) => (
                          <motion.div
                            key={item.key}
                            className="grid grid-cols-5 gap-4 items-center py-3 px-4 bg-white rounded-lg hover:shadow-md transition-shadow"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: (categoryIndex * 0.1) + (itemIndex * 0.05) }}
                          >
                            <div className="text-sm font-medium text-gray-700">{item.name}</div>
                            {enhancedPlans.map((plan, planIndex) => (
                              <motion.div
                                key={plan.name}
                                className="flex items-center justify-center"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: (categoryIndex * 0.1) + (itemIndex * 0.05) + (planIndex * 0.02) }}
                              >
                                <div className="flex items-center space-x-2">
                                  {getFeatureIcon(plan, item.key)}
                                  <span className="text-sm text-gray-600">
                                    {getFeatureValue(plan, item.key)}
                                  </span>
                                </div>
                              </motion.div>
                            ))}
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Action Buttons */}
                <motion.div
                  className="flex justify-center space-x-4 mt-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  {enhancedPlans.map((plan, index) => (
                    <motion.button
                      key={plan.name}
                      className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${plan.name === Subscriptions.pro
                        ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white hover:shadow-lg'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 + (index * 0.1) }}
                    >
                      Choose {plan.name}
                    </motion.button>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PlanComparisonTable;
